import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Block as BlockIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { paginateManage, remove, archive, closeItem, reactivate } from '../../services/announcement.service';
import CustomizedSnackbars from '../../components/material-snackbars';
import SoftTable from '../../components/soft-table';

const AnnouncementManageGrid = ({ intl, history, lang, reloadToken }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchField, setSearchField] = useState('title');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, variant: 'success', message: '' });

  const load = async (pageNum, size, overrides = {}) => {
    const effectiveSize = size || rowsPerPage;
    const effectiveSearchField = overrides.searchField !== undefined ? overrides.searchField : searchField;
    const effectiveSearch = overrides.search !== undefined ? overrides.search : search;
    const effectiveStatusFilter = overrides.statusFilter !== undefined ? overrides.statusFilter : statusFilter;
    setLoading(true);
    try {
      const fields = [];
      const values = [];

      if (effectiveSearch.trim()) {
        fields.push(effectiveSearchField);
        values.push(effectiveSearch.trim());
      }

      if (effectiveStatusFilter) {
        fields.push('status');
        values.push(effectiveStatusFilter);
      }

      const field = fields.join('|');
      const searchStr = values.join('|');

      const r = await paginateManage(pageNum + 1, effectiveSize, field, searchStr);
      const fetchedRows = r?.data?.data ?? [];
      const fetchedTotal = r?.data?.count ?? 0;

      setRows(fetchedRows);
      setTotal(fetchedTotal);
      setPage(pageNum);
      setLoading(false);
    } catch (e) {
      console.error('Error loading manage grid:', e);
      setRows([]);
      setTotal(0);
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
  }, [reloadToken]);

  const statusLabel = (statusId) => {
    const statusMap = {
      1: 'noticeboard.status.scheduled',
      2: 'noticeboard.status.published',
      3: 'noticeboard.status.closed',
      4: 'noticeboard.status.archived',
    };
    return intl.formatMessage({ id: statusMap[statusId] || 'noticeboard.status.unknown' });
  };

  const statusColor = (statusId) => {
    const colorMap = {
      1: 'info',
      2: 'success',
      3: 'default',
      4: 'warning',
    };
    return colorMap[statusId] || 'default';
  };

  const runAction = async () => {
    if (!confirm) return;

    const { action, id } = confirm;
    setActionLoading(true);

    const successMessages = {
      delete: 'Aviso excluído com sucesso!',
      archive: 'Aviso arquivado com sucesso!',
      close: 'Aviso encerrado com sucesso!',
      reactivate: 'Aviso reativado com sucesso!',
    };

    try {
      if (action === 'delete') {
        await remove(id);
      } else if (action === 'archive') {
        await archive(id);
      } else if (action === 'close') {
        await closeItem(id);
      } else if (action === 'reactivate') {
        await reactivate(id);
      }

      setConfirm(null);
      setActionLoading(false);
      setSnack({ open: true, variant: 'success', message: successMessages[action] || 'Operação realizada com sucesso!' });
      load(page);
    } catch (e) {
      console.error('Error running action:', e);
      setConfirm(null);
      setActionLoading(false);
      setSnack({ open: true, variant: 'error', message: intl.formatMessage({ id: 'process.error' }) });
      load(page);
    }
  };

  const handleSearch = () => {
    load(0);
  };

  const handleClear = () => {
    setSearchField('title');
    setSearch('');
    setStatusFilter('');
    load(0, undefined, { searchField: 'title', search: '', statusFilter: '' });
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleChangePage = (_e, newPage) => {
    load(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setRowsPerPage(newSize);
    load(0, newSize);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const rowActionsFor = (row) => {
    if (!row || !row.canedit) return [];
    const A = (id, icon, labelId, onClick) => ({ id, icon, label: intl.formatMessage({ id: labelId }), onClick });
    const actions = [
      A('edit', <EditIcon fontSize="small" />, 'noticeboard.action.edit', () => history.push(`/${lang}/announcement-edit/${row.id}?tab=manage`)),
    ];
    if (row.statusid === 1 || row.statusid === 2) {
      actions.push(A('close', <BlockIcon fontSize="small" />, 'noticeboard.action.close', () => setConfirm({ action: 'close', id: row.id, title: row.title })));
      actions.push(A('archive', <ArchiveIcon fontSize="small" />, 'noticeboard.action.archive', () => setConfirm({ action: 'archive', id: row.id, title: row.title })));
    }
    if (row.statusid === 3 || row.statusid === 4) {
      actions.push(A('reactivate', <ReplayIcon fontSize="small" />, 'noticeboard.action.reactivate', () => setConfirm({ action: 'reactivate', id: row.id, title: row.title })));
    }
    actions.push(A('delete', <DeleteIcon fontSize="small" />, 'noticeboard.action.delete', () => setConfirm({ action: 'delete', id: row.id, title: row.title })));
    return actions;
  };

  const columns = [
    { key: 'title', label: intl.formatMessage({ id: 'noticeboard.manage.column.title' }) },
    { key: 'authorname', label: intl.formatMessage({ id: 'noticeboard.manage.column.author' }), nowrap: true },
    {
      key: 'status',
      label: intl.formatMessage({ id: 'noticeboard.manage.column.status' }),
      render: (row) => <Chip size="small" label={statusLabel(row.statusid)} color={statusColor(row.statusid)} />,
    },
    {
      key: 'publishdate',
      label: intl.formatMessage({ id: 'noticeboard.manage.column.publishDate' }),
      nowrap: true,
      render: (row) => (row.publishdate ? moment(row.publishdate).format('DD/MM/YYYY HH:mm') : ''),
    },
    {
      key: 'organizations',
      label: intl.formatMessage({ id: 'noticeboard.manage.column.organizations' }),
      render: (row) =>
        row.allbyglobaladmin
          ? intl.formatMessage({ id: 'noticeboard.manage.organizations.allGlobal' })
          : row.allorganizations
          ? intl.formatMessage({ id: 'noticeboard.manage.organizations.all' })
          : row.organizationslabel || '',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <Box
        sx={{
          display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
          p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '14px', bgcolor: 'background.paper',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="announcement-search-field-label">
            {'Buscar por'}
          </InputLabel>
          <Select
            labelId="announcement-search-field-label"
            label={'Buscar por'}
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <MenuItem value="title">
              {intl.formatMessage({ id: 'noticeboard.manage.column.title' })}
            </MenuItem>
            <MenuItem value="author">
              {intl.formatMessage({ id: 'noticeboard.manage.column.author' })}
            </MenuItem>
            <MenuItem value="organization">
              {intl.formatMessage({ id: 'noticeboard.manage.column.organizations' })}
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder={intl.formatMessage({ id: 'noticeboard.search.placeholder' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ flex: 1, maxWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="announcement-status-filter-label">
            {intl.formatMessage({ id: 'noticeboard.manage.column.status' })}
          </InputLabel>
          <Select
            labelId="announcement-status-filter-label"
            label={intl.formatMessage({ id: 'noticeboard.manage.column.status' })}
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="">
              {intl.formatMessage({ id: 'noticeboard.manage.filter.all' })}
            </MenuItem>
            <MenuItem value="1">
              {intl.formatMessage({ id: 'noticeboard.status.scheduled' })}
            </MenuItem>
            <MenuItem value="2">
              {intl.formatMessage({ id: 'noticeboard.status.published' })}
            </MenuItem>
            <MenuItem value="3">
              {intl.formatMessage({ id: 'noticeboard.status.closed' })}
            </MenuItem>
            <MenuItem value="4">
              {intl.formatMessage({ id: 'noticeboard.status.archived' })}
            </MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={handleSearch} sx={{ textTransform: 'none', borderRadius: '10px' }}>
          {intl.formatMessage({ id: 'noticeboard.action.search' })}
        </Button>
        <Button variant="text" onClick={handleClear} sx={{ textTransform: 'none', borderRadius: '10px' }}>
          {intl.formatMessage({ id: 'noticeboard.action.clear' })}
        </Button>
      </Box>

      <SoftTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage={intl.formatMessage({ id: 'noticeboard.detail.empty' })}
        actions={rowActionsFor}
        pagination={{
          count: total,
          page,
          rowsPerPage,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
          labelRowsPerPage: 'Itens por página',
        }}
      />

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle>
          {intl.formatMessage({ id: 'noticeboard.action.confirm.title' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm ? intl.formatMessage({ id: `noticeboard.action.confirm.${confirm.action}` }, { title: confirm.title }) : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>
            {intl.formatMessage({ id: 'noticeboard.action.cancel' })}
          </Button>
          <Button
            onClick={runAction}
            variant="contained"
            color="primary"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : intl.formatMessage({ id: 'noticeboard.action.confirm' })}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomizedSnackbars
        variant={snack.variant}
        message={snack.message}
        isOpen={snack.open}
        toClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};

export default AnnouncementManageGrid;
